import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import BreadcrumbNav from '../components/common/BreadcrumbNav';
import SEOHead from '../components/common/SEOHead';
import { BRAND_FULL_NAME, BRAND_NAME } from '../config/brand';

const PrivacyPolicy: React.FC = () => {
  const breadcrumbs = [{ name: 'سياسة الخصوصية', path: '/privacy' }];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <SEOHead
        title={`سياسة الخصوصية | ${BRAND_NAME}`}
        description={`سياسة خصوصية منصة أوائل مصر: كيف نجمع ونستخدم بيانات الطلاب بأمان.`}
        keywords={`سياسة الخصوصية, أوائل مصر, حماية بيانات الطلاب`}
        url="/privacy"
        breadcrumbs={breadcrumbs}
      />

      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
          <BreadcrumbNav items={breadcrumbs} />
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            <Shield className="h-7 w-7 text-green-700" />
            سياسة الخصوصية
          </h1>
        </div>
      </div>

      <article className="mx-auto max-w-4xl space-y-8 px-4 py-8 text-sm leading-relaxed text-gray-700 sm:px-6 sm:text-base lg:px-8">
        <p>
          تحترم {BRAND_FULL_NAME} خصوصية طلابها وأولياء الأمور. توضّح هذه الصفحة كيف نتعامل مع البيانات
          الشخصية عند استخدام المنصة التعليمية.
        </p>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">1. البيانات التي نجمعها</h2>
          <ul className="list-disc space-y-2 pe-5">
            <li>بيانات الحساب: الاسم، رقم الهاتف، البريد الإلكتروني عند التسجيل.</li>
            <li>بيانات التعلّم: الكورسات المشترك بها، تقدّم المشاهدة، نتائج الاختبارات.</li>
            <li>بيانات تقنية: نوع الجهاز، سجلات الدخول، وعنوان IP لأغراض الأمان.</li>
            <li>بيانات الدفع: عبر مزوّد الدفع فقط؛ لا نخزّن بيانات البطاقة الكاملة على خوادمنا.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">2. كيف نستخدم البيانات</h2>
          <ul className="list-disc space-y-2 pe-5">
            <li>تقديم الخدمة التعليمية وإدارة الاشتراكات.</li>
            <li>تحسين جودة المحتوى والتجربة التعليمية.</li>
            <li>التواصل بخصوص الحساب، الدعم الفني، والتحديثات المهمة.</li>
            <li>تمكين متابعة ولي الأمر عند استخدام ميزة المتابعة بموافقة صاحب الحساب.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">3. مشاركة البيانات</h2>
          <p>
            لا نبيع بياناتك الشخصية. قد نشارك الحد الأدنى اللازم مع مزوّدي خدمات الدفع أو الاستضافة
            بموجب اتفاقيات سرية، أو عند طلب قانوني ملزم.
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">4. الحماية والاحتفاظ</h2>
          <p>
            نستخدم إجراءات تقنية وتنظيمية مناسبة لحماية الحسابات. نحتفظ بالبيانات طالما الحساب نشط أو
            بقدر ما يقتضيه تقديم الخدمة والمتطلبات القانونية.
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">5. حقوقك</h2>
          <p>
            يمكنك طلب تصحيح بياناتك أو حذف الحساب عبر صفحة{' '}
            <Link to="/contact" className="font-semibold text-green-800 underline">
              تواصل معنا
            </Link>
            . قد نحتاج التحقق من هويتك قبل تنفيذ الطلب.
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">6. ملفات تعريف الارتباط</h2>
          <p>
            نستخدم ملفات تعريف ضرورية لتشغيل الجلسة والأمان. قد تُستخدم أدوات قياس أداء لتحسين المنصة
            دون بيع بيانات التعريف الشخصية لأطراف إعلانية.
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-xl font-bold text-green-900">7. التحديثات</h2>
          <p>
            قد نحدّث هذه السياسة عند الحاجة. يستمر استخدامك للمنصة بعد النشر يعني اطلاعَك على النسخة
            الأحدث. آخر تحديث: يوليو 2026.
          </p>
        </section>

        <p className="border-t border-gray-200 pt-6 text-sm text-gray-500">
          لمزيد من التفاصيل راجع أيضاً{' '}
          <Link to="/terms" className="text-green-800 underline">
            الشروط والأحكام
          </Link>{' '}
          و{' '}
          <Link to="/refund-policy" className="text-green-800 underline">
            سياسة الاسترجاع
          </Link>
          .
        </p>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
